import { Box, Text, VStack } from "@chakra-ui/react";
import { Field, FormInput, FormTextarea, FormSelect, FormSwitch, CollapsibleSection, ItemCard, ItemHeader, AddButton, RepeatableText, Grid2 } from "@/components/jobOffer/ui";
import { createEmptyObjection } from "@/models/campaign";

const ADDRESS_FORM_OPTIONS = [
    { value: "Formal", label: "Forma grzecznościowa (Pan/Pani)" },
    { value: "Informal", label: "Forma nieformalna (Ty)" },
];
const MESSAGE_LENGTH_OPTIONS = [
    { value: "Short", label: "Krótkie" },
    { value: "Medium", label: "Średnie" },
    { value: "Long", label: "Długie" },
];
const MAX_QUESTIONS_OPTIONS = [1, 2, 3, 4, 5].map(n => ({ value: String(n), label: String(n) }));
const SALES_INITIATIVE_OPTIONS = [
    { value: "Low", label: "Niski -- agent głównie odpowiada, nie naciska" },
    { value: "Medium", label: "Średni -- agent proponuje kolejny krok, nie forsuje" },
    { value: "High", label: "Wysoki -- agent aktywnie sprzedaje i domyka" },
];

const Step6AgentPersonalization = ({ values, setValues, forceOpen }) => {
    const set = (key, val) => setValues({ ...values, [key]: val });

    const style = values.conversationStyle ?? {};
    const setStyle = (patch) => set("conversationStyle", { ...style, ...patch });

    const salesArguments = (values.salesArguments ?? []).map(a => a.argument ?? a);
    const setSalesArguments = (strings) => set("salesArguments", strings.map(s => ({ argument: s, category: null })));

    const objections = values.objections ?? [];
    const setObjections = (next) => set("objections", next);
    const updateObjection = (i, patch) => setObjections(objections.map((o, idx) => idx === i ? { ...o, ...patch } : o));
    const removeObjection = (i) => setObjections(objections.filter((_, idx) => idx !== i));
    const addObjection = () => setObjections([...objections, createEmptyObjection()]);

    const escalation = values.escalationConfig ?? {};
    const setEscalation = (patch) => set("escalationConfig", { ...escalation, ...patch });

    return (
        <VStack w="full" align="stretch" gap={4}>
            <CollapsibleSection title="Tożsamość i styl rozmowy" subtitle="Kim jest Agent, jak się przedstawia i jak długo pisze — opcjonalne, ma sensowne wartości domyślne" defaultOpen forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    <Grid2>
                        <Field label="Imię handlowca AI w rozmowie">
                            <FormInput value={style.agentName ?? ""} onChange={(e) => setStyle({ agentName: e.target.value })} placeholder='np. "Marek"' />
                        </Field>
                        <Field label="Styl marki / branży">
                            <FormInput value={style.brandStyle ?? ""} onChange={(e) => setStyle({ brandStyle: e.target.value })} placeholder='np. "premium, rzeczowy"' />
                        </Field>
                    </Grid2>
                    <Field label="Szablon przedstawiania się" hint="Placeholdery: {imie}, {kampania}, {firma}">
                        <FormTextarea value={style.introductionTemplate ?? ""} onChange={(e) => setStyle({ introductionTemplate: e.target.value })} rows={2} />
                    </Field>
                    <Field label="Forma zwracania się">
                        <FormSelect value={style.addressForm ?? ""} onChange={(v) => setStyle({ addressForm: v || null })} options={ADDRESS_FORM_OPTIONS} placeholder="Wybierz formę" />
                    </Field>
                    <FormSwitch label="Używaj emoji" checked={!!style.useEmoji} onChange={(v) => setStyle({ useEmoji: v })} />
                    <Grid2>
                        <Field label="Długość wiadomości">
                            <FormSelect value={style.messageLength ?? ""} onChange={(v) => setStyle({ messageLength: v || null })} options={MESSAGE_LENGTH_OPTIONS} placeholder="Wybierz długość" />
                        </Field>
                        <Field label="Max. pytań na wiadomość">
                            <FormSelect
                                value={style.maxQuestionsPerMessage != null ? String(style.maxQuestionsPerMessage) : ""}
                                onChange={(v) => setStyle({ maxQuestionsPerMessage: v ? Number(v) : null })}
                                options={MAX_QUESTIONS_OPTIONS}
                                placeholder="Wybierz limit"
                            />
                        </Field>
                    </Grid2>
                    <Field label="Sposób domykania rozmowy">
                        <FormTextarea value={style.closingStyle ?? ""} onChange={(e) => setStyle({ closingStyle: e.target.value })} rows={2} />
                    </Field>
                </VStack>
            </CollapsibleSection>

            <CollapsibleSection title="Inicjatywa sprzedażowa" subtitle="Jak mocno Agent popycha rozmowę w stronę konwersji" forceOpen={forceOpen}>
                <VStack align="stretch" gap={3}>
                    <Field label="Poziom inicjatywy">
                        <FormSelect value={style.salesInitiativeLevel ?? ""} onChange={(v) => setStyle({ salesInitiativeLevel: v || null })} options={SALES_INITIATIVE_OPTIONS} placeholder="Wybierz poziom" />
                    </Field>
                    <Box bg="bg.highlight" borderRadius={8} p={3}>
                        <Text fontSize="xs" color="text.grey">
                            Jeśli Agent nie ma pewnej informacji, ma powiedzieć że musi sprawdzić i przekazać pytanie handlowcowi -- nigdy nie zgadywać.
                        </Text>
                    </Box>
                    <RepeatableText
                        label="Czego Agent nie może wymyślać"
                        values={style.forbiddenAssumptions ?? []}
                        onChange={(v) => setStyle({ forbiddenAssumptions: v })}
                        placeholder='np. "Nie wymyślaj cen"'
                    />
                </VStack>
            </CollapsibleSection>

            <CollapsibleSection title="Argumenty i obiekcje" subtitle="Przewagi używane w rozmowie i jak reagować na zastrzeżenia klienta" forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    <RepeatableText values={salesArguments} onChange={setSalesArguments} placeholder="np. Szybkość wdrożenia" label="Argumenty sprzedażowe" />
                    <VStack align="stretch" gap={3}>
                        {objections.map((o, i) => (
                            <ItemCard key={i}>
                                <ItemHeader title={o.objection || "Nowa obiekcja"} index={i} onRemove={() => removeObjection(i)} />
                                <VStack align="stretch" gap={3}>
                                    <Field label="Obiekcja" required hint='np. "To za drogo"'>
                                        <FormInput value={o.objection} onChange={(e) => updateObjection(i, { objection: e.target.value })} />
                                    </Field>
                                    <Grid2>
                                        <Field label="Sposób odpowiedzi">
                                            <FormInput value={o.responseStyle ?? ""} onChange={(e) => updateObjection(i, { responseStyle: e.target.value })} />
                                        </Field>
                                        <Field label="Alternatywna oferta">
                                            <FormInput value={o.alternativeOffer ?? ""} onChange={(e) => updateObjection(i, { alternativeOffer: e.target.value })} />
                                        </Field>
                                    </Grid2>
                                    <Field label="Argument"><FormTextarea value={o.argument ?? ""} onChange={(e) => updateObjection(i, { argument: e.target.value })} rows={2} /></Field>
                                    <FormSwitch label="Możliwość rabatu" checked={!!o.allowDiscount} onChange={(v) => updateObjection(i, { allowDiscount: v })} />
                                </VStack>
                            </ItemCard>
                        ))}
                        <AddButton label="Dodaj obiekcję" onClick={addObjection} />
                    </VStack>
                </VStack>
            </CollapsibleSection>

            <CollapsibleSection title="Eskalacja do handlowca" subtitle="Kiedy przekazać rozmowę człowiekowi" forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    <RepeatableText label="Warunki eskalacji do handlowca" values={escalation.conditions ?? []} onChange={(v) => setEscalation({ conditions: v })} placeholder='np. "klient gotowy podpisać umowę"' />
                    <Field label="Próg wartości transakcji do eskalacji (PLN)" hint="powyżej tej kwoty Agent przekazuje rozmowę handlowcowi">
                        <FormInput
                            type="number" min="0"
                            value={escalation.transactionValueThreshold ?? ""}
                            onChange={(e) => setEscalation({ transactionValueThreshold: e.target.value === "" ? null : Number(e.target.value) })}
                            placeholder="np. 20000"
                        />
                    </Field>
                    <Field label="Szablon podsumowania przy przekazaniu" hint="dostępne placeholdery: {imie}, {nazwisko}, {firma}, {potrzeba}, {budzet}, {termin}">
                        <FormTextarea value={escalation.handoffSummaryTemplate ?? ""} onChange={(e) => setEscalation({ handoffSummaryTemplate: e.target.value })} rows={4} />
                    </Field>
                </VStack>
            </CollapsibleSection>
        </VStack>
    );
};

export default Step6AgentPersonalization;
